import { useModal } from 'modals';
import { useSnapshot } from 'valtio';

import * as authStore from '@cartwhl/common/stores/authStore';
import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';

import useAppletsUrl from '../../hooks/useAppletsUrl';
import MarketingMedia from '../MarketingMedia';
import NavigationLink from '../NavigationLink';
import {
  type CapabilityContent,
  type ModelActionTarget,
  type ModelCardContent,
  type ModelLearnTarget,
  type ModelMedia,
} from './content';

// The research status is intentionally light in both themes, even on dark cards.
const researchButtonClassName =
  '!bg-white !text-gray-60 !opacity-100 dark:!bg-white dark:!text-gray-60';

interface ModelMediaProps {
  className?: string;
  dataPart?: string;
  media: ModelMedia;
}

export function ModelMediaSlot({
  className = '',
  dataPart,
  media,
}: ModelMediaProps) {
  return (
    <MarketingMedia media={media} className={className} dataPart={dataPart} />
  );
}

interface ModelCtaProps {
  className?: string;
  label: string;
  target: ModelActionTarget;
}

export function ModelCta({ target, label, className }: ModelCtaProps) {
  const { authStatus } = useSnapshot(authStore.state);
  const appletsUrl = useAppletsUrl();

  if (target === 'api') {
    return (
      <Button
        as="a"
        href="https://api-docs.getcartwheel.com/"
        target="_blank"
        rel="noopener noreferrer"
        variant="cta"
        className={className}
      >
        {label}
      </Button>
    );
  }

  if (target === 'mcp') {
    return (
      <Button
        as="a"
        href="https://github.com/Cartwhl/cartwheel-mcp"
        target="_blank"
        rel="noopener noreferrer"
        variant="cta"
        className={className}
      >
        {label}
      </Button>
    );
  }

  if (authStatus !== 'user') {
    return (
      <Button
        as={NavigationLink}
        href="/pricing"
        variant="cta"
        className={className}
      >
        {label}
      </Button>
    );
  }

  const appletPath = {
    'capture-app': '/comic',
    'generation-app': '/generation',
    'editor-app': '/motion-editor',
  }[target];

  return (
    <Button
      as="a"
      href={appletsUrl && `${appletsUrl}${appletPath}`}
      target="_blank"
      rel="noopener noreferrer"
      variant="cta"
      className={className}
      loading={!appletsUrl}
    >
      {label}
    </Button>
  );
}

function ModelLearnAction({
  label,
  modelName,
  target,
}: {
  label: string;
  modelName: string;
  target: ModelLearnTarget;
}) {
  const { open } = useModal();
  const ariaLabel = `${label} about ${modelName}`;

  if (target === 'contact') {
    return (
      <Button
        type="button"
        variant="secondary"
        surface="alternative"
        aria-label={ariaLabel}
        onClick={() => open('ENTERPRISE_CONTACT', { reason: 'request_demo' })}
      >
        {label}
      </Button>
    );
  }

  if (target.startsWith('#')) {
    return (
      <Button
        as="a"
        href={target}
        variant="secondary"
        surface="alternative"
        aria-label={ariaLabel}
      >
        {label}
      </Button>
    );
  }

  return (
    <Button
      as={NavigationLink}
      href={target}
      variant="secondary"
      surface="alternative"
      aria-label={ariaLabel}
    >
      {label}
    </Button>
  );
}

interface SectionLabelProps {
  children: string;
}

export function SectionLabel({ children }: SectionLabelProps) {
  return (
    <Text as="h2" token={{ base: '16/medium', sm: '32/medium' }} leading="body">
      {children}
    </Text>
  );
}

interface ModelCardProps {
  card: ModelCardContent;
  className?: string;
}

export function ModelCard({ card, className = '' }: ModelCardProps) {
  return (
    <article
      className={`bg-secondary flex min-h-[216px] flex-col gap-10 rounded-2xl p-8 sm:min-h-[272px] ${className}`}
    >
      <div className="flex flex-1 flex-col gap-10">
        <Text as="h3" token={{ base: '16', sm: '32' }} leading="body">
          {card.title}
        </Text>
        <Text
          as="p"
          token={{ base: '16', sm: '32' }}
          leading="body"
          className="text-secondary"
        >
          {card.description}
        </Text>
        <div className="w-fit shrink-0">
          {card.action === 'status' ? (
            <Button
              type="button"
              variant="secondary"
              surface="alternative"
              className={researchButtonClassName}
              disabled
            >
              {card.actionLabel}
            </Button>
          ) : card.action === 'try' ? (
            <ModelCta target={card.actionTarget} label={card.actionLabel} />
          ) : (
            <ModelLearnAction
              target={card.actionTarget}
              label={card.actionLabel}
              modelName={card.title}
            />
          )}
        </div>
      </div>
    </article>
  );
}

interface CapabilityCardProps {
  capability: CapabilityContent;
  className?: string;
  mediaClassName?: string;
}

export function CapabilityCard({
  capability,
  className = '',
  mediaClassName = '',
}: CapabilityCardProps) {
  return (
    <article
      className={`bg-secondary flex min-w-0 flex-col justify-between gap-6 rounded-2xl p-3 sm:gap-10 sm:p-8 ${className}`}
    >
      <div className="flex min-h-5 flex-wrap items-start justify-between gap-2 min-[520px]:flex-nowrap min-[520px]:items-center">
        <Text
          as="h3"
          token={{ base: '12', sm: '32' }}
          leading="body"
          className="min-w-0 break-words tracking-[-0.64px] max-[519px]:w-full"
        >
          {capability.title}
        </Text>
        {capability.inResearch && (
          <Button
            type="button"
            variant="secondary"
            surface="alternative"
            className={`${researchButtonClassName} h-[34px] shrink-0 px-3`}
            disabled
          >
            In Research
          </Button>
        )}
      </div>
      <ModelMediaSlot
        media={capability.media}
        className={`mt-auto shrink-0 rounded-lg ${mediaClassName}`}
      />
    </article>
  );
}

export function ActionCard({
  action,
  media,
  title,
  target,
  size = 'default',
}: {
  action: string;
  media: ModelMedia;
  title: string;
  target: ModelActionTarget;
  size?: 'default' | 'swing';
}) {
  const heightClassName =
    size === 'swing'
      ? 'min-h-[312px] sm:min-h-[624px] xl:min-h-[592px] 2xl:min-h-[519px]'
      : 'min-h-[312px] sm:min-h-[624px] xl:min-h-[660px] 2xl:min-h-[732px]';

  return (
    <article
      className={`bg-secondary flex ${heightClassName} flex-col gap-10 rounded-2xl p-8 2xl:py-10`}
    >
      <Text as="h3" token={{ base: '16', sm: '32' }} leading="body">
        {title}
      </Text>
      <ModelCta target={target} label={action} className="w-fit" />
      <ModelMediaSlot
        media={media}
        className="mt-auto aspect-video w-full shrink-0 rounded-lg"
      />
    </article>
  );
}
